import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ForcePasswordChangePage } from "@/pages/auth/ForcePasswordChangePage";
import { RequireAuth, RequireRole, RequirePasswordChanged } from "@/lib/auth/guards";
import { AdminLayout } from "@/components/AdminLayout";
import { PageSkeleton } from "@/components/PageState";

// Eagerly loaded: tiny auth pages needed before JS hydration is complete.
// All admin feature pages are lazy — each is its own JS chunk. The thunks are
// shared between lazy() (downloads a chunk on first navigation) and the idle
// prefetch below (warms every other chunk once the app is idle) so navigating
// between modules feels instant instead of waiting ~1s to fetch the chunk.
const load = {
  dashboard:       () => import("@/pages/admin/DashboardPage").then((m) => ({ default: m.DashboardPage })),
  tenants:         () => import("@/pages/admin/TenantsPage").then((m) => ({ default: m.TenantsPage })),
  tenantDetail:    () => import("@/pages/admin/tenants/TenantDetailPage").then((m) => ({ default: m.TenantDetailPage })),
  tenantApproval:  () => import("@/pages/admin/tenants/TenantApprovalPage").then((m) => ({ default: m.TenantApprovalPage })),
  flats:           () => import("@/pages/admin/FlatsPage").then((m) => ({ default: m.FlatsPage })),
  featureGroups:   () => import("@/pages/admin/FeatureGroupsPage").then((m) => ({ default: m.FeatureGroupsPage })),
  kycApplicationDetail: () => import("@/pages/admin/kyc/KycApplicationDetailPage").then((m) => ({ default: m.KycApplicationDetailPage })),
  inspectionsQueue: () => import("@/pages/admin/inspections/InspectionsQueuePage").then((m) => ({ default: m.InspectionsQueuePage })),  duplicateFlats:  () => import("@/pages/admin/flats/DuplicateFlatsPage").then((m) => ({ default: m.DuplicateFlatsPage })),
  billing:         () => import("@/pages/admin/BillingPage").then((m) => ({ default: m.BillingPage })),
  serviceCharge:   () => import("@/pages/admin/ServiceChargeReportsPage").then((m) => ({ default: m.ServiceChargeReportsPage })),
  invoiceDetail:   () => import("@/pages/admin/billing/InvoiceDetailPage").then((m) => ({ default: m.InvoiceDetailPage })),
  tenancies:       () => import("@/pages/admin/tenancies/TenanciesPage").then((m) => ({ default: m.TenanciesPage })),
  tenancyDetail:   () => import("@/pages/admin/tenancies/TenancyDetailPage").then((m) => ({ default: m.TenancyDetailPage })),
  applications:             () => import("@/pages/admin/applications/ApplicationsPage").then((m) => ({ default: m.ApplicationsPage })),
  rentalApplicationDetail:  () => import("@/pages/admin/rental-applications/RentalApplicationDetailPage").then((m) => ({ default: m.RentalApplicationDetailPage })),
  communications:  () => import("@/pages/admin/CommunicationsPage").then((m) => ({ default: m.CommunicationsPage })),
  noticeDetail:    () => import("@/pages/admin/NoticeDetailPage").then((m) => ({ default: m.NoticeDetailPage })),
  documents:       () => import("@/pages/admin/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
  rentReviews:     () => import("@/pages/admin/RentReviewsPage").then((m) => ({ default: m.RentReviewsPage })),
  reports:         () => import("@/pages/admin/ReportsPage").then((m) => ({ default: m.ReportsPage })),
  audit:           () => import("@/pages/admin/AuditPage").then((m) => ({ default: m.AuditPage })),
  users:           () => import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })),
  settings:        () => import("@/pages/admin/SettingsPage").then((m) => ({ default: m.SettingsPage })),
  sitemap:         () => import("@/pages/admin/SiteMapPage").then((m) => ({ default: m.SiteMapPage })),
};

const DashboardPage            = lazy(load.dashboard);
const TenantsPage              = lazy(load.tenants);
const TenantDetailPage         = lazy(load.tenantDetail);
const TenantApprovalPage       = lazy(load.tenantApproval);
const FlatsPage                = lazy(load.flats);
const FeatureGroupsPage        = lazy(load.featureGroups);
const KycApplicationDetailPage = lazy(load.kycApplicationDetail);
const InspectionsQueuePage     = lazy(load.inspectionsQueue);const DuplicateFlatsPage       = lazy(load.duplicateFlats);
const BillingPage              = lazy(load.billing);
const ServiceChargeReportsPage = lazy(load.serviceCharge);
const InvoiceDetailPage        = lazy(load.invoiceDetail);
const TenanciesPage            = lazy(load.tenancies);
const TenancyDetailPage        = lazy(load.tenancyDetail);
const ApplicationsPage             = lazy(load.applications);
const RentalApplicationDetailPage  = lazy(load.rentalApplicationDetail);
const CommunicationsPage       = lazy(load.communications);
const NoticeDetailPage         = lazy(load.noticeDetail);
const DocumentsPage            = lazy(load.documents);
const RentReviewsPage          = lazy(load.rentReviews);
const ReportsPage              = lazy(load.reports);
const AuditPage                = lazy(load.audit);
const UsersPage                = lazy(load.users);
const SettingsPage             = lazy(load.settings);
const SiteMapPage              = lazy(load.sitemap);

function usePrefetchRoutes() {
  useEffect(() => {
    const prefetch = () => Object.values(load).forEach((fn) => { void fn(); });
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) {
      ric(prefetch, { timeout: 4000 });
    } else {
      const t = setTimeout(prefetch, 2000);
      return () => clearTimeout(t);
    }
  }, []);
}

export function App() {
  usePrefetchRoutes();
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Authenticated, no password-change requirement (so users can unlock) */}
      <Route
        path="/force-password-change"
        element={
          <RequireAuth>
            <ForcePasswordChangePage />
          </RequireAuth>
        }
      />

      {/* Admin app: authenticated + admin role + password changed */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <RequireRole roles={["super_admin", "estate_manager"]}>
              <RequirePasswordChanged>
                <AdminLayout />
              </RequirePasswordChanged>
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
        <Route path="tenants" element={<Suspense fallback={<PageSkeleton />}><TenantsPage /></Suspense>} />
        <Route path="tenants/approval" element={<Suspense fallback={<PageSkeleton />}><TenantApprovalPage /></Suspense>} />
        <Route path="tenants/:id" element={<Suspense fallback={<PageSkeleton />}><TenantDetailPage /></Suspense>} />
        <Route path="flats" element={<Suspense fallback={<PageSkeleton />}><FlatsPage /></Suspense>} />
        <Route path="flats/duplicates" element={<Suspense fallback={<PageSkeleton />}><DuplicateFlatsPage /></Suspense>} />
        <Route path="feature-groups" element={<Suspense fallback={<PageSkeleton />}><FeatureGroupsPage /></Suspense>} />
        <Route path="kyc-review/:id" element={<Suspense fallback={<PageSkeleton />}><KycApplicationDetailPage /></Suspense>} />
        <Route path="inspections" element={<Suspense fallback={<PageSkeleton />}><InspectionsQueuePage /></Suspense>} />        <Route path="tenancies" element={<Suspense fallback={<PageSkeleton />}><TenanciesPage /></Suspense>} />
        <Route path="tenancies/:id" element={<Suspense fallback={<PageSkeleton />}><TenancyDetailPage /></Suspense>} />
        <Route path="applications" element={<Suspense fallback={<PageSkeleton />}><ApplicationsPage /></Suspense>} />
        <Route path="rental-applications/:id" element={<Suspense fallback={<PageSkeleton />}><RentalApplicationDetailPage /></Suspense>} />
        {/* Pre-consolidation bookmarks: the three former standalone pages are now tabs on one Applications page */}
        <Route path="rental-applications" element={<Navigate to="/applications?tab=applications" replace />} />
        <Route path="kyc-review" element={<Navigate to="/applications?tab=kyc" replace />} />
        <Route path="boarding" element={<Navigate to="/applications?tab=boarding" replace />} />
        <Route path="billing" element={<Suspense fallback={<PageSkeleton />}><BillingPage /></Suspense>} />
        <Route path="billing/:id" element={<Suspense fallback={<PageSkeleton />}><InvoiceDetailPage /></Suspense>} />
        <Route path="service-charge" element={<Suspense fallback={<PageSkeleton />}><ServiceChargeReportsPage /></Suspense>} />
        <Route path="communications" element={<Suspense fallback={<PageSkeleton />}><CommunicationsPage /></Suspense>} />
        <Route path="communications/:id" element={<Suspense fallback={<PageSkeleton />}><NoticeDetailPage /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<PageSkeleton />}><DocumentsPage /></Suspense>} />
        <Route path="rent-reviews" element={<Suspense fallback={<PageSkeleton />}><RentReviewsPage /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<PageSkeleton />}><ReportsPage /></Suspense>} />
        <Route path="audit" element={<Suspense fallback={<PageSkeleton />}><AuditPage /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<PageSkeleton />}><UsersPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense>} />
        <Route path="sitemap" element={<Suspense fallback={<PageSkeleton />}><SiteMapPage /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
